import { useState } from "react";
import { motion } from "framer-motion";
import { MailCheck, PartyPopper, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const inputCls =
    "rounded-xl border-2 border-monster-navy bg-white px-4 py-3 font-semibold text-monster-navy shadow-sticker-sm focus-visible:ring-monster-blue placeholder:text-monster-navy/35 placeholder:font-normal";

export const LaunchList = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/subscribers", { name, email });
            setDone(true);
            toast.success("You're on the launch list!");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="launch-list" data-testid="launch-list-section" className="border-b-2 border-monster-navy bg-monster-yellow py-20 sm:py-24">
            <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ type: "spring", stiffness: 90, damping: 16 }}
                >
                    <span className="inline-flex items-center gap-2 rounded-full border-2 border-monster-navy bg-monster-navy px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.2em] text-monster-yellow">
                        <PartyPopper className="h-4 w-4" /> Grand Opening
                    </span>
                    <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-monster-navy sm:text-5xl lg:text-6xl">
                        Opening <span className="text-monster-blue">Spring</span> <span className="text-monster-green">2027</span>
                    </h2>
                    <p className="mt-5 max-w-lg text-base font-semibold text-monster-navy/75 md:text-lg">
                        Join our email list and receive <span className="font-display text-monster-red">20% off a rental</span> when we launch.
                        Be first in line for the bounciest summer Genesee County has ever seen.
                    </p>
                    <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border-2 border-monster-navy bg-white px-5 py-3 shadow-sticker-sm">
                        <Tag className="h-5 w-5 text-monster-green" />
                        <span className="font-display text-sm font-semibold text-monster-navy">Launch-list price lock: your 20% off never expires in 2027</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40, rotate: 1.5 }}
                    whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ type: "spring", stiffness: 90, damping: 15, delay: 0.1 }}
                    className="rounded-[32px] border-2 border-monster-navy bg-white p-7 shadow-sticker-lg sm:p-9"
                >
                    {done ? (
                        <div data-testid="launch-list-success" className="py-6 text-center">
                            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-monster-navy bg-monster-green shadow-sticker">
                                <MailCheck className="h-9 w-9 text-monster-navy" />
                            </span>
                            <h3 className="mt-6 font-display text-3xl font-semibold text-monster-navy">You're on the list!</h3>
                            <p className="mx-auto mt-3 max-w-sm text-sm font-semibold text-monster-navy/60">
                                Check your inbox — we just sent you a confirmation. We'll email you again the moment we open in Spring 2027, with your 20% off code inside.
                            </p>
                        </div>
                    ) : (
                        <form data-testid="launch-list-form" onSubmit={submit} className="space-y-5">
                            <h3 className="font-display text-2xl font-semibold text-monster-navy">Join the launch list</h3>
                            <div>
                                <Input
                                    data-testid="launch-list-name-input"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Your name"
                                    aria-label="Your name"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <Input
                                    data-testid="launch-list-email-input"
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@email.com"
                                    aria-label="Your email"
                                    className={inputCls}
                                />
                            </div>
                            <button
                                data-testid="launch-list-submit-btn"
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-full border-2 border-monster-navy bg-monster-blue py-4 font-display text-lg font-semibold text-white shadow-sticker transition-all hover:-translate-y-0.5 hover:bg-[#1a63c2] active:translate-y-1 active:shadow-none disabled:opacity-60"
                            >
                                {loading ? "Joining..." : "Get My 20% Off"}
                            </button>
                            <p className="text-center text-xs font-semibold text-monster-navy/45">One email at launch. No spam, ever.</p>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
